/**
 * PHASE 4: Auth-Aware Fetch Wrapper
 * Offline-capable fetch for write operations
 */

import { enqueueApiAction } from './queueService';
import { shouldAttemptNetworkOperation, getNetworkStatus } from './network';
import { generateUUID } from './db';

/**
 * Offline-capable fetch options
 * @typedef {Object} OfflineOptions
 * @property {string} type - Action type (TIMECLOCK_PUNCH, AVAILABILITY_UPDATE, etc.)
 * @property {string} [entityType] - Entity type for deduplication
 * @property {string} [entityId] - Entity ID for deduplication
 * @property {number} [priority] - Priority (lower = higher priority)
 * @property {string} [successMessage] - Toast message on success
 * @property {string} [offlineMessage] - Toast message when queued offline
 * @property {boolean} [requiresAuth] - Whether auth token is required
 * @property {'immediate'|'online'|'manual'} [replayStrategy] - When to replay
 */

/**
 * Normalized response shape
 * @typedef {Object} OfflineResponse
 * @property {boolean} ok - Whether request succeeded
 * @property {boolean} [queued] - Whether request was queued for later
 * @property {boolean} [offline] - Whether we were offline
 * @property {number} [status] - HTTP status code
 * @property {any} [data] - Response data
 * @property {string} [error] - Error message
 * @property {string} [queueItemId] - ID of queued item
 */

/**
 * Wrapper around fetch for write operations with offline support
 * @param {string} url - Request URL
 * @param {RequestInit} options - Fetch options
 * @param {OfflineOptions} [offlineOptions] - Offline handling options
 * @returns {Promise<OfflineResponse>}
 */
export async function offlineCapableFetch(url, options = {}, offlineOptions = {}) {
  const method = (options.method || 'GET').toUpperCase();
  const isWriteOperation = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);
  
  // For read operations, just do normal fetch
  if (!isWriteOperation) {
    return normalFetch(url, options);
  }
  
  // Check if we should attempt network operation
  const networkStatus = getNetworkStatus();
  const shouldAttempt = shouldAttemptNetworkOperation();
  
  // Generate idempotency key for write operations
  const idempotencyKey = offlineOptions.idempotencyKey || generateUUID();
  
  // If online and should attempt, try the request
  if (shouldAttempt) {
    try {
      const response = await attemptFetch(url, options, idempotencyKey);
      
      if (response.ok) {
        return {
          ok: true,
          queued: false,
          offline: false,
          status: response.status,
          data: response.data
        };
      }
      
      // If server error (5xx), queue for retry if offline options provided
      if (response.status >= 500 && offlineOptions.type) {
        return await queueForLater(url, options, offlineOptions, idempotencyKey);
      }
      
      // Return error response
      return {
        ok: false,
        queued: false,
        offline: false,
        status: response.status,
        error: response.error || `Request failed with status ${response.status}`,
        data: response.data
      };
      
    } catch (networkError) {
      // Network error - queue if offline options provided
      console.log('[OfflineFetch] Network error, checking queue option...');
      
      if (offlineOptions.type) {
        return await queueForLater(url, options, offlineOptions, idempotencyKey);
      }
      
      return {
        ok: false,
        queued: false,
        offline: true,
        error: networkError.message
      };
    }
  }
  
  // Offline - queue if offline options provided
  if (offlineOptions.type) {
    return await queueForLater(url, options, offlineOptions, idempotencyKey);
  }
  
  // Offline with no queue option
  return {
    ok: false,
    queued: false,
    offline: true,
    error: 'Network unavailable and no offline handling configured'
  };
}

/**
 * Queue a request for later when offline
 * @returns {Promise<OfflineResponse>}
 */
async function queueForLater(url, options, offlineOptions, idempotencyKey) {
  try {
    const payload = options.body ? JSON.parse(options.body) : {};
    
    const { item } = await enqueueApiAction({
      type: offlineOptions.type,
      endpoint: url,
      method: options.method || 'POST',
      payload,
      headers: options.headers || {},
      entityType: offlineOptions.entityType,
      entityId: offlineOptions.entityId,
      userId: offlineOptions.userId,
      merchantId: offlineOptions.merchantId,
      priority: offlineOptions.priority ?? 5,
      requiresAuth: offlineOptions.requiresAuth ?? true,
      idempotencyKey
    });
    
    console.log(`[OfflineFetch] Queued ${offlineOptions.type}: ${item.id}`);
    
    return {
      ok: true, // Considered success because it's queued
      queued: true,
      offline: true,
      queueItemId: item.id,
      data: { message: offlineOptions.offlineMessage || 'Saved offline. Will sync when online.' }
    };
    
  } catch (queueError) {
    console.error('[OfflineFetch] Failed to queue:', queueError);
    return {
      ok: false,
      queued: false,
      offline: true,
      error: 'Failed to save offline: ' + queueError.message
    };
  }
}

/**
 * Attempt a fetch request with idempotency key
 */
async function attemptFetch(url, options, idempotencyKey) {
  const headers = {
    'Content-Type': 'application/json',
    'x-idempotency-key': idempotencyKey,
    ...options.headers
  };
  
  // Add auth token if present
  const token = getAuthToken();
  if (token && !headers['Authorization']) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(url, {
    ...options,
    headers
  });
  
  let data = null;
  try {
    data = await response.json();
  } catch (e) {
    // Response might not be JSON
  }
  
  return {
    ok: response.ok,
    status: response.status,
    data,
    error: data?.message || data?.error
  };
}

/**
 * Normal fetch for read operations
 */
async function normalFetch(url, options) {
  try {
    const headers = {
      ...options.headers
    };
    
    const token = getAuthToken();
    if (token && !headers['Authorization']) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(url, { ...options, headers });
    
    let data = null;
    try {
      data = await response.json();
    } catch (e) {}
    
    return {
      ok: response.ok,
      status: response.status,
      data,
      error: data?.message || data?.error
    };
    
  } catch (error) {
    return {
      ok: false,
      offline: !navigator.onLine,
      error: error.message
    };
  }
}

/**
 * Convenience method: Queue when offline, otherwise fetch normally
 * Same as offlineCapableFetch but with clearer naming
 */
export async function queueWhenOffline(url, options = {}, offlineOptions = {}) {
  return offlineCapableFetch(url, options, offlineOptions);
}

/**
 * Create a pre-configured offline-capable fetcher for a specific action type
 */
export function createOfflineFetcher(defaultOfflineOptions) {
  return async (url, options = {}, overrideOptions = {}) => {
    return offlineCapableFetch(url, options, {
      ...defaultOfflineOptions,
      ...overrideOptions
    });
  };
}

// Helper to get auth token
function getAuthToken() {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem('staff_token') || 
         localStorage.getItem('backoffice_token') ||
         localStorage.getItem('auth_token');
}

export default {
  offlineCapableFetch,
  queueWhenOffline,
  createOfflineFetcher
};
